import * as React from 'react';
import { ControlOutlined } from '@ant-design/icons';
import Button from 'antd/lib/button';
import ConfigProvider from 'antd/lib/config-provider';
import Flex from 'antd/lib/flex';
import Modal from 'antd/lib/modal';
import Switch from 'antd/lib/switch';
import Tooltip from 'antd/lib/tooltip';
import Text from 'antd/lib/typography/Text';
import antdTheme from 'antd/lib/theme';
import { useSelector } from 'react-redux';
import { RootState } from '../../../reducers/reducers';
import { selectUserIsRunwayAdmin } from '../../../selectors/selectors';
import { antGrey, getAntdThemeTokens } from '../../styles/globalStyles';
import { useSidebarColors, useThemeMode } from '../../styles/themeMode';
import {
    FEATURE_FLAG_FORCE_OFF_VALUE,
    FeatureFlag,
    FeatureFlagVariantValue,
    getValueForFlag,
    setInternalFeatureFlagOverride,
    useInternalFeatureFlagOverrides,
} from '../../../utils/internalFeatureFlags';

const FEATURE_FLAG_LABELS: Record<FeatureFlag, string> = {
    [FeatureFlag.OrgOverviewRevamp]: 'Org overview',
    [FeatureFlag.OrgOverviewDigest]: 'Org overview digest',
    [FeatureFlag.OrgOverviewInsights]: 'Org overview insights',
};

const InternalFeatureFlagRow: React.FC<{ flag: FeatureFlag }> = ({ flag }) => {
    const sc = useSidebarColors();
    const overrides = useInternalFeatureFlagOverrides();
    const overrideValue = overrides[flag];
    const isEnabled = getValueForFlag(flag) === FeatureFlagVariantValue.On;

    return (
        <Flex
            align="center"
            justify="space-between"
            style={{
                border: `1px solid ${sc.border}`,
                borderRadius: 8,
                padding: '12px 14px',
            }}
        >
            <Flex vertical gap={2}>
                <Text style={{ fontWeight: 600 }}>{FEATURE_FLAG_LABELS[flag]}</Text>
                <Text style={{ color: sc.textSecondary, fontSize: 12 }}>
                    {overrideValue ?
                        `Override: ${overrideValue === FeatureFlagVariantValue.On ? 'On' : 'Off'}`
                    :   'Using default evaluation'}
                </Text>
            </Flex>

            <Flex align="center" gap={8}>
                {overrideValue && (
                    <Button
                        type="text"
                        size="small"
                        onClick={() => setInternalFeatureFlagOverride(flag)}
                        style={{ color: antGrey.grey7 }}
                    >
                        Reset
                    </Button>
                )}
                <Switch
                    checked={isEnabled}
                    checkedChildren="On"
                    unCheckedChildren="Off"
                    onChange={(checked) =>
                        setInternalFeatureFlagOverride(
                            flag,
                            checked ? FeatureFlagVariantValue.On : FEATURE_FLAG_FORCE_OFF_VALUE,
                        )
                    }
                />
            </Flex>
        </Flex>
    );
};

const useCanShowInternalFeatureFlags = () => {
    const isRunwayAdmin = useSelector((state: RootState) => selectUserIsRunwayAdmin(state));
    return isRunwayAdmin;
};

export const InternalFeatureFlagsButton: React.FC<{ onClick: () => void }> = ({ onClick }) => {
    const canShowInternalFeatureFlags = useCanShowInternalFeatureFlags();
    const sc = useSidebarColors();

    if (!canShowInternalFeatureFlags) {
        return null;
    }

    return (
        <Tooltip title="Internal feature flags">
            <Button
                type="text"
                aria-label="Internal feature flags"
                onClick={onClick}
                style={{ padding: 0, height: 'auto', color: sc.text, justifyContent: 'start' }}
                icon={<ControlOutlined style={{ fontSize: 18 }} />}
            />
        </Tooltip>
    );
};

export const InternalFeatureFlagsModal: React.FC<{ open: boolean; onClose: () => void }> = ({ open, onClose }) => {
    const canShowInternalFeatureFlags = useCanShowInternalFeatureFlags();
    const { mode } = useThemeMode();
    const theme = mode === 'dark' ? 'dark' : 'light';

    if (!canShowInternalFeatureFlags) {
        return null;
    }

    return (
        <ConfigProvider
            theme={{
                algorithm: theme === 'dark' ? antdTheme.darkAlgorithm : antdTheme.defaultAlgorithm,
                token: getAntdThemeTokens(theme),
            }}
        >
            <Modal title="Internal feature flags" open={open} onCancel={onClose} footer={null} width={560}>
                <Flex vertical gap={12}>
                    {Object.values(FeatureFlag).map((flag) => (
                        <InternalFeatureFlagRow key={flag} flag={flag as FeatureFlag} />
                    ))}
                </Flex>
            </Modal>
        </ConfigProvider>
    );
};

export const InternalFeatureFlagsControl: React.FC = () => {
    const [isModalOpen, setIsModalOpen] = React.useState(false);

    return (
        <>
            <InternalFeatureFlagsButton onClick={() => setIsModalOpen(true)} />
            <InternalFeatureFlagsModal open={isModalOpen} onClose={() => setIsModalOpen(false)} />
        </>
    );
};

export default InternalFeatureFlagsControl;
